/*
Drives the asynchronous solve lifecycle for every run started this session.

Solving is not synchronous, and runs are queued client-side: clicking Run enqueues
a run (enqueueOptimization) with a snapshot of the scenario; this hook's queue
controller submits queued runs to the backend as concurrency slots free up (staying
within the per-plan cap so we never trip the backend's 429), then polls /run_status
for each in-flight run, auto-opens the results of the run the user is awaiting when
it succeeds, and surfaces failures.

Mount it once at the app/layout level (not on the model page) so runs keep
progressing — and still land — if the user navigates elsewhere while they run.
*/
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUser } from "@clerk/clerk-react";

import { addNotification } from "store/reducers/inputReducer";
import { fetchRunStatus, fetchRunResults, solveOptimization } from "store/reducers/thunks/optimizationThunks";
import {
  selectSolveJobs,
  selectAwaitedRunId,
  MAX_ACTIVE_SOLVES_SIGNED_IN,
  MAX_ACTIVE_SOLVES_SIGNED_OUT,
} from "store/reducers/uiReducer";
import { selectUserLimits } from "store/reducers/selectors/userSelectors";
import { resolveMaxConcurrentSolves } from "functions/entitlements/featureEntitlements";

// Status polling ramps: a run is usually picked up by a worker in well under a
// second, so a flat multi-second interval leaves the UI showing "Queued" for a
// run that's already solving (and a short solve can finish before the second poll
// lands). Poll fast at first, then back off so a long solve isn't hammered.
// Cheap to do: fetchRunStatus is a no-op in the reducer when nothing changed, so
// the extra polls cause no re-renders.
const POLL_RAMP = [
  { untilMs: 10000, everyMs: 750 },
  { untilMs: 60000, everyMs: 3000 },
];
const POLL_INTERVAL_FALLBACK_MS = 5000;

const pollDelayFor = (elapsedMs) => {
  const step = POLL_RAMP.find((s) => elapsedMs < s.untilMs);
  return step ? step.everyMs : POLL_INTERVAL_FALLBACK_MS;
};

const ACTIVE_STATUSES = ["queued", "running"];

// Solver errors can be very long — the "these trips have no travel cost" one
// enumerates every offending trip by name and runs to several hundred
// characters. A toast is not the place for that, so keep the first sentence and
// say there is more. The full text stays on the run in the tracker.
const MAX_REASON_CHARS = 180;
function trimReason(reason) {
  const text = String(reason).trim();
  if (text.length <= MAX_REASON_CHARS) return text;
  const cut = text.slice(0, MAX_REASON_CHARS);
  const lastStop = cut.lastIndexOf(". ");
  const head = lastStop > 60 ? cut.slice(0, lastStop + 1) : cut.trimEnd() + "…";
  return `${head} (see the run for details)`;
}

// Kill-switch for the poller (set false to fully disable status polling / auto-open).
// The memory runaway it guarded against is fixed: the poller now lives in an
// isolated leaf component (SolvePoller) so its updates don't re-render the layout
// or map, and fetchRunStatus is a no-op when a poll returns an unchanged status.
const POLLER_ENABLED = true;

export function useSolvePoller() {
  const dispatch = useDispatch();
  const jobs = useSelector(selectSolveJobs);
  const awaitedRunId = useSelector(selectAwaitedRunId);
  const { isSignedIn } = useUser();

  // Plan-aware: the cap comes from the user's own limits (/user_plan), falling
  // back to these constants when signed out or when the value is absent. It used
  // to be `isSignedIn ? 2 : 1` regardless of plan, so a plan advertising a
  // higher cap silently delivered 2.
  const userLimits = useSelector(selectUserLimits);
  const cap = resolveMaxConcurrentSolves({
    isSignedIn,
    limits: userLimits,
    signedOutFallback: MAX_ACTIVE_SOLVES_SIGNED_OUT,
    signedInFallback: MAX_ACTIVE_SOLVES_SIGNED_IN,
  });

  // Runs we've already auto-opened or notified about, so we act once each.
  const handledRef = useRef(new Set());

  // Bumped by the backoff timer below to re-run the queue controller when a
  // 429-requeued run's backoff elapses (jobs don't change on their own by then).
  const [retryTick, setRetryTick] = useState(0);

  // Stable key of active, *submitted* run ids (local-queued runs have no run_id yet
  // and aren't polled), so the polling effect only resets when the in-flight set
  // actually changes.
  const activeKey = jobs
    .filter((j) => j.runId && ACTIVE_STATUSES.includes(j.status))
    .map((j) => j.runId)
    .join(",");

  // Queue controller: submit queued runs to the backend whenever a concurrency slot
  // is free. Runs are stored newest-first, so we submit the oldest (end of the list)
  // first (FIFO). Marking a job `submitting` (in the thunk's pending reducer) keeps
  // it from being picked twice.
  useEffect(() => {
    if (!POLLER_ENABLED) return;
    const now = Date.now();
    const inFlight = jobs.filter((j) => j.runId && ACTIVE_STATUSES.includes(j.status)).length;
    const submitting = jobs.filter((j) => j.submitting).length;
    let slots = cap - inFlight - submitting;
    if (slots <= 0) return;
    // Skip runs still in a 429 backoff window (nextRetryAt in the future).
    const pending = jobs.filter(
      (j) => !j.runId && !j.submitting && j.status === "queued" && (!j.nextRetryAt || j.nextRetryAt <= now)
    );
    for (let i = pending.length - 1; i >= 0 && slots > 0; i -= 1) {
      dispatch(solveOptimization({ id: pending[i].id }));
      slots -= 1;
    }
  }, [dispatch, jobs, cap, retryTick]);

  // A run the backend refused with 429 is held with a future nextRetryAt; nothing
  // else will change `jobs` until then, so schedule a tick to re-run the controller
  // the moment the earliest backoff elapses.
  useEffect(() => {
    if (!POLLER_ENABLED) return undefined;
    const now = Date.now();
    const waits = jobs
      .filter((j) => !j.runId && j.status === "queued" && j.nextRetryAt && j.nextRetryAt > now)
      .map((j) => j.nextRetryAt - now);
    if (!waits.length) return undefined;
    const timer = setTimeout(() => setRetryTick((t) => t + 1), Math.min(...waits) + 50);
    return () => clearTimeout(timer);
  }, [jobs]);

  // Poll status for all in-flight runs.
  useEffect(() => {
    if (!POLLER_ENABLED) return undefined;
    if (!activeKey) return undefined;
    const runIds = activeKey.split(",");
    let cancelled = false;
    let timer;
    const startedAt = Date.now();
    const poll = () => {
      if (cancelled) return;
      runIds.forEach((runId) => dispatch(fetchRunStatus({ runId })));
      // Re-arm on a ramping delay rather than a fixed interval.
      timer = setTimeout(poll, pollDelayFor(Date.now() - startedAt));
    };
    poll(); // check immediately, then on the ramp
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dispatch, activeKey]);

  // Auto-open the awaited run's results the moment it succeeds.
  useEffect(() => {
    if (!POLLER_ENABLED) return;
    if (!awaitedRunId || handledRef.current.has(awaitedRunId)) return;
    const job = jobs.find((j) => j.runId === awaitedRunId);
    if (job && job.status === "succeeded") {
      handledRef.current.add(awaitedRunId);
      dispatch(fetchRunResults({ runId: awaitedRunId }));
    }
  }, [dispatch, awaitedRunId, jobs]);

  // Surface failures once (successes show in the tracker / auto-open; cancels are
  // user-initiated, so neither needs a toast). Keyed by the stable client id, since
  // a run can fail before it ever gets a run_id.
  useEffect(() => {
    if (!POLLER_ENABLED) return;
    jobs.forEach((job) => {
      if (job.status === "failed" && !handledRef.current.has(job.id)) {
        handledRef.current.add(job.id);
        dispatch(addNotification({
          notificationMsg: job.error
            ? `Couldn’t run “${job.scenarioName}”. ${trimReason(job.error)}`
            : `Couldn’t run “${job.scenarioName}”.`,
          notificationType: "error",
        }));
      }
    });
  }, [dispatch, jobs]);
}

export default useSolvePoller;
